import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import AuthLayout from './AuthPageLayout';
import { pillButtonClass } from './authControls';
import { useAuth } from '../../contexts/useAuth';
import { businessMembersApi } from '../../services/businessMembersApi';

/**
 * Where the emailed invitation link lands.
 *
 * The token says which ACCOUNT to join; it does not say who is joining — so this has to be done
 * signed in. Someone with no login is sent to register first and comes back with the token intact.
 * The server checks the signed-in user's email against the one the invitation was addressed to,
 * so a forwarded link cannot put the wrong person on a customer's account.
 */
export default function AcceptInvitation() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, isLoading, refreshUser } = useAuth();
    const token = searchParams.get('token') ?? '';

    const [status, setStatus] = useState<'working' | 'done' | 'error'>('working');
    const [message, setMessage] = useState('');
    const [businessName, setBusinessName] = useState('');
    const attemptedRef = useRef(false);

    useEffect(() => {
        if (attemptedRef.current || isLoading) return;

        if (!token) {
            attemptedRef.current = true;
            setStatus('error');
            setMessage('That link is missing its invitation code. Use the link from your email.');
            return;
        }

        // Not signed in: register or sign in first, then come straight back with the token. The
        // `next` round-trip is what stops the invitation being lost at the door.
        if (!user) {
            const next = encodeURIComponent(`/accept-invitation?token=${encodeURIComponent(token)}`);
            navigate(`/login?next=${next}`, { replace: true });
            return;
        }

        attemptedRef.current = true;
        businessMembersApi.accept(token)
            .then(async (result) => {
                setBusinessName(result.businessName);
                setStatus('done');
                // The new membership has to reach the auth context, or the app still thinks they
                // belong to nothing.
                await refreshUser();
            })
            .catch((err: unknown) => {
                setStatus('error');
                setMessage(err instanceof Error && err.message
                    ? err.message
                    : 'That invitation could not be accepted.');
            });
    }, [token, user, isLoading, navigate, refreshUser]);

    return (
        <>
            <PageMeta title="Accept invitation" description="Join a business account on DAconnect" />
            <AuthLayout>
                <div className="w-full max-w-[420px] text-center">
                    {status === 'working' && (
                        <>
                            <h1 className="font-display text-[28px] leading-[1.2] font-medium text-ink">
                                Checking your invitation…
                            </h1>
                            <p className="mt-2.5 text-[13.5px] text-ink-muted">One moment.</p>
                        </>
                    )}

                    {status === 'done' && (
                        <>
                            <div className="mx-auto mb-5 grid size-14 place-items-center rounded-full bg-leaf-pill text-2xl text-sidebar">✓</div>
                            <h1 className="font-display text-[28px] leading-[1.2] font-medium text-ink">
                                <b className="font-semibold text-sidebar dark:text-leaf">You're in</b>
                            </h1>
                            <p className="mt-2.5 mb-6 text-[13.5px] leading-[1.6] text-ink-muted">
                                You now have access to {businessName || 'the account'} on DAconnect.
                            </p>
                            <button type="button" onClick={() => navigate('/')} className={`w-full ${pillButtonClass}`}>
                                GO TO DASHBOARD
                            </button>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <h1 className="font-display text-[28px] leading-[1.2] font-medium text-ink">
                                We couldn't accept that invitation
                            </h1>
                            <p className="mt-2.5 mb-6 text-[13.5px] leading-[1.6] text-ink-muted">{message}</p>
                            <button type="button" onClick={() => navigate('/')} className={`w-full ${pillButtonClass}`}>
                                GO TO DASHBOARD
                            </button>
                        </>
                    )}
                </div>
            </AuthLayout>
        </>
    );
}
